import './MapCurrentLine.less';
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks';
import {
  addMiddlepoint,
  addPointToLine,
  deletePointOfLineSelected,
  modifyPointOfLine,
  saveCurrentShape,
  selectAddingGeometryType,
  selectCurrentLine,
  selectEditingGeometryId,
  selectPointOfLine,
  selectPointOfLineSelected,
} from '../../../store/slice/mapSlice';
import { LatLong } from '../../../models/Geometries';
import { Layer, Marker, Source } from 'react-map-gl';
import { useRef, useState } from 'react';
import { Button, Tooltip } from 'antd';
import Icon from '@ant-design/icons';
import { ReactComponent as Remove } from '../../../res/svg/remove.svg';

interface Props {
  mousePosition: LatLong | undefined;
  onDragChange: (dragging: boolean) => any;
}

function MapCurrentLine({ mousePosition, onDragChange }: Props) {
  const dispatch = useAppDispatch();
  const line: LatLong[] = useAppSelector(selectCurrentLine);
  const addingGeometryType = useAppSelector(selectAddingGeometryType);
  const pointOfLineSelected: number = useAppSelector(selectPointOfLineSelected);
  const editingGeometryId: number | undefined = useAppSelector(
    selectEditingGeometryId,
  );
  const coords = line.map((x) => [x.longitud, x.latitud]);
  const [draggingPoint, setDraggingPoint] = useState<number>(-1);
  const [draggingMiddlePoint, setDragginMiddlePoint] = useState<number>(-1);
  const [dragLocation, setDragLocation] = useState<LatLong | undefined>();
  const selectedPointTimeOutRef = useRef<any>();

  if (draggingPoint > -1 && dragLocation) {
    coords[draggingPoint] = [dragLocation.longitud, dragLocation.latitud];
  }

  const lineCoords = [...coords];

  if (mousePosition && !editingGeometryId) {
    lineCoords.push([mousePosition.longitud, mousePosition.latitud]);
  }

  let midpoints = [];

  if (editingGeometryId) {
    if (addingGeometryType === 'poligono') {
      lineCoords.push(lineCoords[0]);
    }

    for (let i = 0; i < lineCoords.length - 1; i++) {
      const x = lineCoords[i];
      const y = lineCoords[i + 1];
      const middle = [(x[0] + y[0]) / 2, (x[1] + y[1]) / 2];
      midpoints.push(middle);
    }
  }

  if (draggingMiddlePoint > -1 && dragLocation) {
    midpoints[draggingMiddlePoint] = [
      dragLocation.longitud,
      dragLocation.latitud,
    ];
    lineCoords.splice(draggingMiddlePoint + 1, 0, [
      dragLocation.longitud,
      dragLocation.latitud,
    ]);
  }

  return (
    <div>
      {coords.map(([long, lat], i) => {
        const isFloatingPoint = i === line.length;
        const isPointSelected = pointOfLineSelected === i;
        return (
          <Marker
            key={i}
            draggable={true}
            onDragStart={(e) => {
              setDraggingPoint(i);
              onDragChange(true);
            }}
            onDrag={(e) => {
              setDragLocation({
                latitud: e.lngLat.lat,
                longitud: e.lngLat.lng,
              });
            }}
            onDragEnd={() => {
              setDraggingPoint(-1);
              dispatch(modifyPointOfLine({ index: i, latLong: dragLocation! }));
              onDragChange(false);
            }}
            onClick={(e) => {
              if (
                addingGeometryType === 'poligono' &&
                i === 0 &&
                line.length >= 3
              ) {
                dispatch(addPointToLine(line[0]));
                dispatch(saveCurrentShape());
              } else {
                dispatch(selectPointOfLine(i));
                clearTimeout(selectedPointTimeOutRef.current);
                selectedPointTimeOutRef.current = setTimeout(() => {
                  dispatch(selectPointOfLine(-1));
                }, 2000);
              }
              e.originalEvent.stopPropagation();
            }}
            longitude={
              draggingPoint === i && dragLocation ? dragLocation.longitud : long
            }
            latitude={
              draggingPoint === i && dragLocation ? dragLocation.latitud : lat
            }
          >
            <div
              className={
                'at-map-current-line__point ' +
                (isPointSelected ? 'selected' : '') +
                (isFloatingPoint ? 'floating' : '')
              }
            ></div>
            <div
              className={
                'at-map-current-line__delete-container ' +
                (isPointSelected ? 'selected' : '')
              }
            >
              {isPointSelected ? (
                <Tooltip title={'Eliminar'} placement={'right'}>
                  <Button
                    className={'at-map-current-line__delete '}
                    size="large"
                    shape="round"
                    onClick={() => dispatch(deletePointOfLineSelected())}
                    type={'ghost'}
                    icon={<Icon component={Remove} />}
                  ></Button>
                </Tooltip>
              ) : null}
            </div>
          </Marker>
        );
      })}
      {midpoints.map(([long, lat], i) => {
        const isFloatingPoint = i === line.length;
        const isPointSelected = pointOfLineSelected === i;
        return (
          <Marker
            key={i}
            draggable={true}
            onDragStart={(e) => {
              setDragginMiddlePoint(i);
              onDragChange(true);
            }}
            onDrag={(e) => {
              setDragLocation({
                latitud: e.lngLat.lat,
                longitud: e.lngLat.lng,
              });
            }}
            onDragEnd={() => {
              setDragginMiddlePoint(-1);
              dispatch(addMiddlepoint({ index: i, latLong: dragLocation! }));
              onDragChange(false);
            }}
            onClick={(e) => {
              e.originalEvent.preventDefault();
              e.originalEvent.stopPropagation();
              dispatch(
                addMiddlepoint({
                  index: i,
                  latLong: { latitud: lat, longitud: long }!,
                }),
              );
            }}
            longitude={long}
            latitude={lat}
          >
            <div
              className={
                'at-map-current-line__middlepoint ' +
                (isPointSelected ? 'selected' : '') +
                (isFloatingPoint ? 'floating' : '')
              }
            ></div>
          </Marker>
        );
      })}
      <Source
        id="my-data"
        type="geojson"
        data={{
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [...lineCoords],
          },
          properties: {},
        }}
      >
        <Layer
          id={'map-current-line'}
          beforeId={'order-current-line'}
          type={'line'}
          paint={{
            'line-color': '#262626',
            'line-width': 2,
          }}
        />
        <Layer
          id={'map-current-line-clickable'}
          beforeId={'order-current-line'}
          type={'line'}
          paint={{
            'line-color': 'transparent',
            'line-width': 12,
          }}
        />
      </Source>
    </div>
  );
}

export default MapCurrentLine;
